import { Fragment, FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { v4 as uuidv4 } from "uuid";

import { Button } from "primereact/button";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { Message } from "primereact/message";

import { faFileArrowDown } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { fieldNonexistentOrMatchingFilter } from "../../components/Shared";
import { translationKeys } from "../../models/app-translation-keys";
import { UploadFile } from "../../models/classes/upload-file.class";
import { extractAudio, getAudioDuration, IAudioExtractionResponse } from "../../models/functions";
import {
  IFileProgressError,
  IFileProgressSuccess,
  ILanguageOption,
  IProgressedFileResult,
} from "../../models/interfaces";
import { useStore } from "../../stores/StoreContext";

import "./TranscriptionUpload.scss";

import { FFmpeg } from "@ffmpeg/ffmpeg";
import { HubConnectionState } from "@microsoft/signalr";

import { faChevronRight } from "@fortawesome/pro-solid-svg-icons";

import { ApiResponse, InitOverrideFunction, SignalREventNames } from "../../../@api";

import { IAsyncConnection } from "../../components/Shared/AsyncConnections/async.connection.interface";
import { TextMarkdown } from "../../components/Shared/Components";
import { TooltipWrapper } from "../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import {
  ICustomProgressColumn,
  IProgressColumn,
  ProgressHandler,
} from "../../components/Shared/ProgressHandler/ProgressHandler";
import { sortTranslatedOptions } from "../../models/functions/sort-translated-option.function";
import { useTranscriptionService } from "../../services/ServiceHooks/transcriptionHook";

interface ITranscriptionUploadProps {
  disabled: boolean;
  uploadFile: UploadFile;
  setIsFileProcessing: (id: string, isProcessing: boolean) => void;
  languageOptions?: ILanguageOption[];
  initialLanguage?: ILanguageOption;
  downloadFunction: (file: UploadFile, content: IProgressedFileResult) => void;
}

const RawTranscriptionUploadComponent: FunctionComponent<ITranscriptionUploadProps> = (
  props: ITranscriptionUploadProps,
) => {
  const { t, i18n } = useTranslation();
  const { budgetStore, transcriptionStore, globalErrorsStore } = useStore();
  const transcriptionService = useTranscriptionService();

  const [ffmpeg, setFfmpeg] = useState<FFmpeg>();
  const [isSignalRActive, setIsSignalRActive] = useState<boolean>(false);
  const [budgetLimitReached, setBudgetLimitReached] = useState<boolean>(false);
  const [progressedFileResult, setProgressedFileResult] = useState<IProgressedFileResult | undefined>(undefined);
  const [isFileProcessing, setIsFileProcessing] = useState<boolean>(false);

  const processIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | undefined>(undefined);
  const transcriptionInProgressRef = useRef<boolean>(false);

  const [translatedLanguageOptions, setTranslatedLanguageOptions] = useState<ILanguageOption[]>([]);
  const [selectedAudioLanguage, setSelectedAudioLanguage] = useState<ILanguageOption | undefined>(
    props.initialLanguage ?? (props.languageOptions?.length ? props.languageOptions[0] : undefined),
  );

  // #53348 Translate and Sort languages
  const getTranslatedAndSortedOptions = (options: ILanguageOption[]) =>
    sortTranslatedOptions(
      options.map(lang => ({ ...lang, label: t(lang.labelKey) })),
      i18n.language,
    );

  useEffect(() => {
    if (!props.languageOptions) {
      return;
    }

    const translatedLanguageOptionsNew = getTranslatedAndSortedOptions(props.languageOptions);

    setTranslatedLanguageOptions(translatedLanguageOptionsNew);

    if (!selectedAudioLanguage) {
      setSelectedAudioLanguage(translatedLanguageOptionsNew[0]);
    } else {
      const currentLanguageOption = translatedLanguageOptionsNew.find(
        lang => lang.code === selectedAudioLanguage?.code,
      );
      setSelectedAudioLanguage(currentLanguageOption);
    }
  }, [i18n.language]);

  useEffect(() => {
    if (!isFileProcessing && transcriptionStore.containsProgressingFile(props.uploadFile)) {
      transcriptionStore.removeProgressingFile(props.uploadFile);
    }
    props.setIsFileProcessing(props.uploadFile.id, isFileProcessing);
  }, [isFileProcessing]);

  useEffect(() => {
    // Cleanup function to cancel ongoing file upload and stop open signalR connections before unmounting
    return () => {
      cancelFileUploadAndOpenProcesses();
    };
  }, []);

  const setDefaultProgressValues = () => {
    processIdRef.current = null;
    transcriptionInProgressRef.current = false;
    setIsFileProcessing(false);
  };

  const setResult = (result: IProgressedFileResult | undefined) => {
    setProgressedFileResult(result);
  };

  const startProcessingFile = useCallback(() => {
    setDefaultProgressValues();

    // Process ID is generated to track the translation process via SignalR and for cancellation if needed
    processIdRef.current = uuidv4();
    transcriptionInProgressRef.current = false;

    transcriptionStore.addProgressingFile(props.uploadFile);
    setResult(undefined);
    setIsSignalRActive(false);
    setIsFileProcessing(true);
  }, []);

  const handleFileAudioExtractionError = (audioExtractFileResponse: IAudioExtractionResponse) => {
    console.error("[Transcription] Audio extraction failed:", audioExtractFileResponse.error);
    setResult({
      errorMessage: t(`ffmpeg.errors.${audioExtractFileResponse.error!.toString()}`),
    });

    setDefaultProgressValues();
    transcriptionStore.removeProgressingFile(props.uploadFile);
  };

  useEffect(() => {
    const startUpload = async () => {
      try {
        abortControllerRef.current = new AbortController();

        if (!ffmpeg) {
          throw new Error("[Transcription] FFmpeg instance is not initialized.");
        }

        const audioExtractFileResponse = await extractAudio(ffmpeg, props.uploadFile.file);
        if (!audioExtractFileResponse.success || !audioExtractFileResponse.audioFile) {
          handleFileAudioExtractionError(audioExtractFileResponse);
          return;
        }

        const audioDuration = await getAudioDuration(audioExtractFileResponse.audioFile);

        const requestInitOverrides: RequestInit | InitOverrideFunction = {
          signal: abortControllerRef.current.signal,
          body: audioExtractFileResponse.audioFile,
        };

        console.debug("[Transcription] Starting file upload for transcription:");
        const response: ApiResponse<string> = await transcriptionService.transcribe(
          {
            processId: processIdRef.current!,
            fileName: encodeURIComponent(audioExtractFileResponse.audioFile.name),
            fileSizeInBytes: audioExtractFileResponse.audioFile.size,
            duration: audioDuration,
            audioLanguage: selectedAudioLanguage?.combinedCode,
          },
          requestInitOverrides,
        );

        abortControllerRef.current = undefined;

        if (response.raw.ok) {
          console.debug(
            "[Transcription] File uploaded successfully and transcription started. Waiting for SignalR events...",
          );
        } else if (response.raw.status === 402) {
          cancelFileUploadAndOpenProcesses();

          handleBudgetLimitReached(response.raw);
          setResult({
            errorMessage: t(translationKeys.globalErrors.budgetErrors.outOfBudget),
          });
        } else {
          cancelFileUploadAndOpenProcesses();

          const error = await response.raw.json();
          console.error("[Transcription] File upload failed. Error: ", error);
          setResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      } catch (error: unknown) {
        cancelFileUploadAndOpenProcesses();

        if (error === 499) {
          console.debug("[Transcription] File upload aborted");
        } else {
          console.error("[Transcription] Error uploading file:", error);
          setResult({
            errorMessage: t(translationKeys.upload.error.uploadFailedMarkdown),
          });
        }
      }
    };

    if (isFileProcessing && isSignalRActive) {
      startUpload();
    }
  }, [isFileProcessing, isSignalRActive]);

  const handleBudgetLimitReached = (response: Response) => {
    budgetStore.setBudgetDisabled(true);
    budgetStore.errorCode = response.status;
    globalErrorsStore.addErrorIfNotExists(translationKeys.globalErrors.budgetErrors.outOfBudget);
    setBudgetLimitReached(true);
  };

  const cancelFileProcessingInBackend = useCallback(async (processId: string) => {
    try {
      await transcriptionService.cancelActiveAudioTranscription({
        processId,
      });

      console.debug("[Transcription] Transcription processing cancelled successfully.");
    } catch (error) {
      console.error("[Transcription] Failed to cancel transcription processing", error);
    }
  }, []);

  const cancelFileUploadAndOpenProcesses = useCallback(() => {
    if (!(transcriptionStore.isAnyFileProgressing && processIdRef.current)) {
      console.debug("[Transcription] No process ID found when trying to cancel file upload and open processes.");
      return;
    }

    console.debug("[Transcription] Cancel file upload and stop open signalR connections");

    abortControllerRef.current?.abort(499);
    transcriptionStore.removeProgressingFile(props.uploadFile);

    // #62480 call cancellation in backend only when transcription is in progress, to avoid cancellation calls when FFmpeg processing and upload are still in progress, as cancellation of FFmpeg processing and upload is handled by aborting the fetch request via abortController
    if (transcriptionInProgressRef.current) {
      cancelFileProcessingInBackend(processIdRef.current);
    }

    setDefaultProgressValues();
  }, [processIdRef.current]);

  const progressHandlerMemo = useMemo(() => {
    if (!isFileProcessing) {
      return;
    }

    const ffmpeg = new FFmpeg();
    setFfmpeg(ffmpeg);

    const processIdFilter = fieldNonexistentOrMatchingFilter("processId", processIdRef.current);

    const successCallback = (res: IFileProgressSuccess) => {
      console.debug("[Transcription] succeeded: ", res);
      setResult({
        languageOption: selectedAudioLanguage,
        successMessage: res.result,
      });

      setDefaultProgressValues();
    };

    const errorCallback = (res: IFileProgressError) => {
      console.error("[Transcription] failed: ", res);
      setResult({
        errorMessage: `${res.error}`,
      });

      setDefaultProgressValues();
    };

    const columns: (IProgressColumn | ICustomProgressColumn)[] = [
      {
        titleKey: translationKeys.upload.progress.convert,
        setupConnections: (progressCallback: (progress: number) => void): IAsyncConnection => {
          ffmpeg.on("progress", ({ progress, time }: { progress: number; time: number }) => {
            console.debug(`[Transcription] FFmpeg transcoding progress: ${(progress * 100).toFixed(1)}%`);
            progressCallback(progress * 100);
          });

          return {
            stop: () => {
              ffmpeg.off("progress", () => {});
              console.debug("[Transcription] FFmpeg processing stopped.");
            },
            state: HubConnectionState.Connected,
          };
        },
      },
      {
        titleKey: translationKeys.upload.progress.upload,
        hubName: "uploadfile",
        filter: [processIdFilter],
        progress: 0,
        progressEndpoint: SignalREventNames.UploadFileProgress,
        successEndpoint: SignalREventNames.UploadFileSucceeded,
        errorEndpoint: SignalREventNames.UploadFileFailed,
        successCallback: () => console.debug("[Transcription] Upload completed successfully"),
        errorCallback: () => console.debug("[Transcription] Upload failed"),
      },
      {
        titleKey: translationKeys.upload.progress.transcribe,
        hubName: "transcription",
        filter: [processIdFilter],
        progress: 0,
        progressEndpoint: SignalREventNames.TranscriptionProgress,
        successEndpoint: SignalREventNames.TranscriptionSucceeded,
        errorEndpoint: SignalREventNames.TranscriptionFailed,
        successCallback,
        progressCallback: () => {
          transcriptionInProgressRef.current = true;
          console.debug("[Transcription] Transcription is in progress...");
        },
        errorCallback,
      },
    ];

    return (
      <ProgressHandler
        progresscolumns={columns}
        cancelProgress={cancelFileUploadAndOpenProcesses}
        connectionSetupCompleted={() => setIsSignalRActive(true)}
      />
    );
  }, [isFileProcessing]);

  return (
    <div className="transcription-processing-container">
      <div className="upload-controls">
        {!!props.languageOptions?.length && (
          <div className="language-selection">
            <Dropdown
              value={selectedAudioLanguage}
              id="audioLanguageDropdown"
              onChange={(e: DropdownChangeEvent) => setSelectedAudioLanguage(e.target.value)}
              options={translatedLanguageOptions}
              optionLabel="label"
              disabled={isFileProcessing || props.disabled}
              tooltip={t(translationKeys.upload.selectAudioLanguage.tooltip)}
              tooltipOptions={{ position: "right" }}
            />
          </div>
        )}

        <Button
          label={t(translationKeys.upload.transcription.upload)}
          onClick={startProcessingFile}
          disabled={!selectedAudioLanguage || isFileProcessing || props.disabled}
          className="flex flex-row-reverse align-items-center gap-2"
          icon={<FontAwesomeIcon icon={faChevronRight} />}
        />
      </div>

      {progressHandlerMemo}

      {budgetLimitReached && (
        <Message severity="error" text={t(translationKeys.globalErrors.budgetErrors.outOfBudget)} />
      )}

      {progressedFileResult && (
        <div className="output-area">
          <div className="output-header">
            <span className="output-title">
              {t(translationKeys.upload.transcription.output)}

              {progressedFileResult.languageOption && (
                <small>&nbsp;({progressedFileResult.languageOption?.code?.toUpperCase()})</small>
              )}
            </span>

            {!!progressedFileResult.successMessage?.length && (
              <Fragment>
                <TooltipWrapper
                  targetRef=".output-download-icon"
                  position="top"
                  content={t(translationKeys.upload.transcription.download)}
                />
                <FontAwesomeIcon
                  className="output-download-icon"
                  icon={faFileArrowDown}
                  onClick={() => props.downloadFunction(props.uploadFile, progressedFileResult!)}
                />
              </Fragment>
            )}
          </div>

          <div className="output-content">
            {!!progressedFileResult.successMessage?.length && (
              <TextMarkdown className="success-message">{progressedFileResult.successMessage}</TextMarkdown>
            )}

            {!!progressedFileResult.errorMessage?.length && (
              <TextMarkdown className="error-message">{progressedFileResult.errorMessage}</TextMarkdown>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const TranscriptionUploadComponent = observer(RawTranscriptionUploadComponent);
